import {
  DeveloperTool, DeveloperToolType, Viewer,
} from '@stereograph/teiaviewer';
import { AddGuidePathCommand } from '@stereograph/teiaviewer-plugins';
import { BindingApi, ButtonApi } from '@tweakpane/core';
import { Vector3 } from 'three';
import { FolderApi, Pane } from 'tweakpane';
import { PathPointsHelper } from './PathPointsHelper';

export class PathDeveloperTool extends DeveloperTool {
  override readonly name = 'path';
  override readonly type: DeveloperToolType = 'dev';

  private readonly params = {
    point: new Vector3(),
    pointCount: 0,
  };

  private readonly points = new Array<Vector3>();
  private viewer: Viewer | null = null;
  private pointsHelper: PathPointsHelper | null = null;
  private folder: FolderApi | null = null;
  private pointCountBinding: BindingApi | null = null;
  private removePointButton: ButtonApi | null = null;
  private clearPointsButton: ButtonApi | null = null;
  private createPathButton: ButtonApi | null = null;

  override setup(viewer: Viewer, pane: Pane): void {
    this.viewer = viewer;
    this.pointsHelper = new PathPointsHelper(viewer);
    this.folder = pane.addFolder({
      title: 'Guide path',
      expanded: false,
    });

    this.folder.addBinding(this.params, 'point', {
      label: 'Next point',
    });

    this.pointCountBinding = this.folder.addBinding(this.params, 'pointCount', {
      label: 'Points',
      readonly: true,
      format: (value) => value.toFixed(0),
    });

    this.folder.addButton({
      title: 'Add point',
    }).on('click', this.addPoint);

    this.removePointButton = this.folder.addButton({
      title: 'Remove last point',
    }).on('click', this.removeLastPoint);

    this.clearPointsButton = this.folder.addButton({
      title: 'Clear points',
    }).on('click', this.clearPoints);

    this.createPathButton = this.folder.addButton({
      title: 'Create path',
    }).on('click', this.createPath);

    this.updateControls();
  }

  override dispose(): void {
    this.pointsHelper?.dispose();
    this.pointsHelper = null;
    this.folder?.dispose();
    this.folder = null;
    this.viewer = null;
    this.points.length = 0;
  }

  private readonly addPoint = () => {
    const point = this.params.point.clone();
    this.points.push(point);
    this.pointsHelper?.addPoint(point);
    this.updateControls();
  };

  private readonly removeLastPoint = () => {
    this.points.pop();
    this.pointsHelper?.removeLastPoint();
    this.updateControls();
  };

  private readonly clearPoints = () => {
    this.points.length = 0;
    this.pointsHelper?.clear();
    this.updateControls();
  };

  private readonly createPath = () => {
    if (!this.viewer || this.points.length < 2) return;

    new AddGuidePathCommand(this.viewer, this.points).execute();
    this.clearPoints();
  };

  private updateControls() {
    this.params.pointCount = this.points.length;
    this.pointCountBinding?.refresh();

    const hasPoints = this.points.length > 0;
    if (this.removePointButton) this.removePointButton.disabled = !hasPoints;
    if (this.clearPointsButton) this.clearPointsButton.disabled = !hasPoints;
    if (this.createPathButton) this.createPathButton.disabled = this.points.length < 2;
  }
}
